import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  Btn, Card, CardHeader, Table, Tr, Td, Badge, StatusBadge,
  FormGroup, Input, Select, Textarea, Modal, StatCard,
  Spinner, SectionDivider, Alert, fmtAmt, fmtDate
} from '../components/ui';
import { accountsAPI, partiesAPI } from '../utils/api';

// ── shared hook ──────────────────────────────────────────
function useData(fn, deps = []) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    setLoading(true);
    fn().then(r => setData(r.data)).catch(() => toast.error('Load failed')).finally(() => setLoading(false));
  }, deps); // eslint-disable-line
  useEffect(() => { reload(); }, [reload]);
  return [data, loading, reload];
}

// ── helpers ──────────────────────────────────────────────
const fyStart = () => {
  const m = new Date().getMonth() + 1;
  const y = new Date().getFullYear();
  return `${m >= 4 ? y : y - 1}-04-01`;
};
const today = () => new Date().toISOString().split('T')[0];
const numFmt = v => parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const amt = v => '₹ ' + numFmt(v);
const typeColor = { asset: 'blue', liability: 'red', equity: 'purple', income: 'green', expense: 'amber' };

// ── DateRange picker ─────────────────────────────────────
function DateRange({ from, to, onFrom, onTo }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <FormGroup label="From">
        <Input type="date" value={from} onChange={e => onFrom(e.target.value)} />
      </FormGroup>
      <FormGroup label="To">
        <Input type="date" value={to} onChange={e => onTo(e.target.value)} />
      </FormGroup>
    </div>
  );
}

// ── Print wrapper ────────────────────────────────────────
function PrintBtn({ label = '🖨 Print' }) {
  return <Btn variant="outline" size="sm" onClick={() => window.print()}>{label}</Btn>;
}

// ── Statement header ─────────────────────────────────────
function StmtHeader({ title, subtitle }) {
  return (
    <div className="text-center mb-6 pb-4 border-b-2 border-slate-800 print:block">
      <div className="font-black text-xl text-slate-800">Mehta Global Exports Ltd.</div>
      <div className="text-sm text-slate-500 mt-0.5">GSTIN: 27AAACM1234F1ZX | IEC: 0512034567 | Mumbai</div>
      <div className="font-bold text-lg text-blue-700 mt-3">{title}</div>
      {subtitle && <div className="text-sm text-slate-500 mt-0.5">{subtitle}</div>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ACCOUNTS DASHBOARD
// ══════════════════════════════════════════════════════════
export function AccountsDashboard() {
  const [from, setFrom] = useState(fyStart());
  const [to, setTo]     = useState(today());
  const [plData, plLoading]   = useData(() => accountsAPI.profitLoss(from, to), [from, to]);
  const [bsData, bsLoading]   = useData(() => accountsAPI.balanceSheet(from, to), [from, to]);
  const [bankData, bankLoading] = useData(() => accountsAPI.bankSummary(from, to), [from, to]);

  const pl = plData || {};
  const bs = bsData || {};
  const banks = bankData?.banks || [];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div>
          <h1 className="text-xl font-black text-slate-800">📒 Accounts Overview</h1>
          <p className="text-sm text-slate-400">Financial position at a glance</p>
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
        </div>
      </div>

      {plLoading || bsLoading ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <StatCard icon="💹" label="Revenue" value={amt(pl.total_income)} color="green" />
            <StatCard icon="💸" label="Total Expenses" value={amt(pl.total_expense)} color="red" />
            <StatCard icon="📈" label="Gross Profit" value={amt(pl.gross_profit)} color={parseFloat(pl.gross_profit) >= 0 ? 'green' : 'red'} sub={pl.total_direct_income > 0 ? `GP% ${((pl.gross_profit / pl.total_direct_income) * 100).toFixed(1)}%` : ''} />
            <StatCard icon="🏆" label="Net Profit" value={amt(pl.net_profit)} color={parseFloat(pl.net_profit) >= 0 ? 'green' : 'red'} sub={pl.total_income > 0 ? `NP% ${((pl.net_profit / pl.total_income) * 100).toFixed(1)}%` : ''} />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <StatCard icon="🏦" label="Total Assets" value={amt(bs.total_assets)} color="blue" />
            <StatCard icon="📋" label="Total Liabilities" value={amt(bs.total_liabilities)} color="amber" />
            <StatCard icon="💎" label="Equity + Reserves" value={amt(bs.total_equity)} color="purple" />
            <StatCard icon="💰" label="Bank Balance" value={amt(bankData?.total_balance)} color="green" />
          </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Bank Balances */}
        <Card>
          <CardHeader title="🏦 Bank & Cash Balances" />
          {bankLoading ? <Spinner /> : (
            <Table headers={['Account', 'Type', 'Balance']}>
              {banks.map(b => (
                <Tr key={b.id}>
                  <Td><span className="font-semibold">{b.name}</span></Td>
                  <Td><Badge color={b.sub_type === 'bank' ? 'blue' : 'green'}>{b.sub_type}</Badge></Td>
                  <Td right><span className={`font-bold ${b.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>{amt(b.balance)}</span></Td>
                </Tr>
              ))}
              <Tr>
                <Td colSpan={2}><strong>Total</strong></Td>
                <Td right><strong className="text-blue-700">{amt(bankData?.total_balance)}</strong></Td>
              </Tr>
            </Table>
          )}
        </Card>

        {/* Quick P&L Summary */}
        <Card>
          <CardHeader title="📊 P&L Quick View" />
          {plLoading ? <Spinner /> : (
            <div className="p-4 space-y-2 text-sm">
              {[
                ['Export Revenue',    pl.total_direct_income,   'text-green-600'],
                ['Other Income',      pl.total_other_income,    'text-green-500'],
                ['Total Income',      pl.total_income,          'text-green-700 font-bold border-t border-slate-200 pt-2'],
                ['Cost of Goods',     pl.total_direct_expense,  'text-red-500'],
                ['Gross Profit',      pl.gross_profit,          'text-blue-700 font-bold'],
                ['Operating Expenses',pl.total_indirect_expense,'text-red-500'],
                ['Net Profit / Loss', pl.net_profit,            parseFloat(pl.net_profit) >= 0 ? 'text-green-700 font-black text-base border-t-2 border-slate-800 pt-2' : 'text-red-700 font-black text-base border-t-2 border-slate-800 pt-2'],
              ].map(([label, val, cls]) => (
                <div key={label} className={`flex justify-between py-1.5 border-b border-slate-50 ${cls}`}>
                  <span>{label}</span>
                  <span>{amt(val)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// CHART OF ACCOUNTS
// ══════════════════════════════════════════════════════════
export function ChartOfAccounts() {
  const [data, loading, reload] = useData(() => accountsAPI.chart());
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState({ code: '', name: '', type: 'income', sub_type: '', parent_id: '', is_group: 0, opening_balance: 0, opening_type: 'Dr', description: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const accounts = (data?.accounts || []).filter(a =>
    (!typeFilter || a.type === typeFilter) &&
    (!search || a.name.toLowerCase().includes(search.toLowerCase()) || a.code.includes(search))
  );

  const subTypes = {
    asset:     ['fixed_asset','current_asset','bank','cash','receivable','inventory','advance','gst','tax','prepaid','deposit'],
    liability: ['long_term','current','payable','loan','bank_od','gst','tax','advance','provision'],
    equity:    ['capital','retained','profit','reserve'],
    income:    ['revenue','other'],
    expense:   ['direct','indirect'],
  };

  const save = async () => {
    if (!form.code || !form.name || !form.type) return toast.error('Code, Name and Type required');
    setSaving(true);
    try {
      if (form.id) { await accountsAPI.chartUpdate(form.id, form); toast.success('Updated'); }
      else { await accountsAPI.chartCreate(form); toast.success('Account created'); }
      setModal(false); reload();
    } catch (e) { toast.error(e?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const groupedByType = ['asset','liability','equity','income','expense'].map(t => ({
    type: t, accounts: accounts.filter(a => a.type === t)
  })).filter(g => g.accounts.length > 0);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">📂 Chart of Accounts</h1><p className="text-sm text-slate-400">Double-entry ledger accounts — Assets, Liabilities, Income, Expenses</p></div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Input placeholder="🔍 Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 180 }} />
          <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ width: 130 }}>
            <option value="">All Types</option>
            {['asset','liability','equity','income','expense'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
          </Select>
          <Btn onClick={() => { setForm({ code:'',name:'',type:'income',sub_type:'',parent_id:'',is_group:0,opening_balance:0,opening_type:'Dr',description:'' }); setModal(true); }}>+ Add Account</Btn>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div className="space-y-4">
          {groupedByType.map(g => (
            <Card key={g.type}>
              <div className={`px-5 py-3 border-b font-bold text-sm flex items-center gap-2 bg-slate-50`}>
                <Badge color={typeColor[g.type]}>{g.type.toUpperCase()}</Badge>
                <span className="text-slate-700">{g.accounts.length} accounts</span>
              </div>
              <Table headers={['Code','Account Name','Type','Sub Type','Group?','Opening Bal','Active','Actions']}>
                {g.accounts.map(a => (
                  <Tr key={a.id}>
                    <Td mono>{a.code}</Td>
                    <Td><span className={`font-semibold ${a.is_group ? 'text-blue-700' : ''}`}>{a.is_group ? '📁 ' : ''}{a.name}</span></Td>
                    <Td><Badge color={typeColor[a.type]}>{a.type}</Badge></Td>
                    <Td><span className="text-xs text-slate-400">{a.sub_type}</span></Td>
                    <Td>{a.is_group ? <Badge color="blue">Group</Badge> : '—'}</Td>
                    <Td right>{a.opening_balance != 0 ? `${a.opening_type} ${numFmt(a.opening_balance)}` : '—'}</Td>
                    <Td>{a.is_active ? <Badge color="green">✓</Badge> : <Badge color="red">✗</Badge>}</Td>
                    <Td>
                      <Btn size="sm" variant="ghost" onClick={() => { setForm(a); setModal(true); }}>Edit</Btn>
                    </Td>
                  </Tr>
                ))}
              </Table>
            </Card>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={form.id ? 'Edit Account' : 'New Account'} size="md"
        footer={<><Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn><Btn loading={saving} onClick={save}>Save Account</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Account Code" required><Input value={form.code} onChange={e => setForm(f=>({...f,code:e.target.value}))} placeholder="e.g. 6010" /></FormGroup>
          <FormGroup label="Account Name" required><Input value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Export Sales Revenue" /></FormGroup>
          <FormGroup label="Type" required>
            <Select value={form.type} onChange={e => setForm(f=>({...f,type:e.target.value,sub_type:''}))}>
              {['asset','liability','equity','income','expense'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Sub Type">
            <Select value={form.sub_type} onChange={e => setForm(f=>({...f,sub_type:e.target.value}))}>
              <option value="">— Select —</option>
              {(subTypes[form.type] || []).map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Opening Balance"><Input type="number" value={form.opening_balance} onChange={e => setForm(f=>({...f,opening_balance:e.target.value}))} /></FormGroup>
          <FormGroup label="Dr / Cr">
            <Select value={form.opening_type} onChange={e => setForm(f=>({...f,opening_type:e.target.value}))}>
              <option value="Dr">Debit (Dr)</option>
              <option value="Cr">Credit (Cr)</option>
            </Select>
          </FormGroup>
          <FormGroup label="Is Group Account?">
            <Select value={form.is_group} onChange={e => setForm(f=>({...f,is_group:e.target.value}))}>
              <option value={0}>No — Ledger Account</option>
              <option value={1}>Yes — Group Account</option>
            </Select>
          </FormGroup>
          <FormGroup label="Active?">
            <Select value={form.is_active ?? 1} onChange={e => setForm(f=>({...f,is_active:e.target.value}))}>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </Select>
          </FormGroup>
        </div>
        <div className="mt-3"><FormGroup label="Description"><Textarea value={form.description||''} onChange={e=>setForm(f=>({...f,description:e.target.value}))} /></FormGroup></div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// JOURNAL VOUCHER
// ══════════════════════════════════════════════════════════
export function JournalVouchers() {
  const [from, setFrom] = useState(fyStart());
  const [to, setTo]     = useState(today());
  const [vType, setVType] = useState('');
  const [data, loading, reload] = useData(() => accountsAPI.vouchers({ from, to, voucher_type: vType }), [from, to, vType]);
  const [accData] = useData(() => accountsAPI.chart());
  const [partyData] = useData(() => partiesAPI.list());
  const [modal, setModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [form, setForm] = useState({
    voucher_type: 'journal', voucher_date: today(), narration: '',
    ref_number: '', lines: [
      { account_id: '', debit: '', credit: '', narration: '', party_id: '' },
      { account_id: '', debit: '', credit: '', narration: '', party_id: '' },
    ]
  });
  const [saving, setSaving] = useState(false);

  const vouchers = data?.vouchers || [];
  const accounts = accData?.accounts || [];
  const parties  = partyData?.parties || [];
  const totalDr  = form.lines.reduce((s, l) => s + parseFloat(l.debit || 0), 0);
  const totalCr  = form.lines.reduce((s, l) => s + parseFloat(l.credit || 0), 0);
  const isBalanced = Math.abs(totalDr - totalCr) < 0.01 && totalDr > 0;

  const setLine = (i, field, val) => setForm(f => {
    const lines = [...f.lines];
    lines[i] = { ...lines[i], [field]: val };
    return { ...f, lines };
  });

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { account_id: '', debit: '', credit: '', narration: '', party_id: '' }] }));
  const delLine = i => setForm(f => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));

  const typeMap = { journal:'Journal Voucher', payment:'Payment Voucher', receipt:'Receipt Voucher', contra:'Contra Entry', sales:'Sales Entry', purchase:'Purchase Entry', debit_note:'Debit Note', credit_note:'Credit Note' };

  const save = async () => {
    if (!isBalanced) return toast.error('Voucher not balanced');
    const validLines = form.lines.filter(l => l.account_id && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0));
    if (validLines.length < 2) return toast.error('At least 2 lines required');
    setSaving(true);
    try {
      await accountsAPI.voucherCreate({ ...form, lines: validLines });
      toast.success('Voucher saved!');
      setModal(false); reload();
    } catch (e) { toast.error(e?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">📝 Journal / Vouchers</h1><p className="text-sm text-slate-400">All accounting vouchers — JV, PV, RV, Contra</p></div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Select value={vType} onChange={e => setVType(e.target.value)} style={{ width: 160 }}>
            <option value="">All Types</option>
            {Object.entries(typeMap).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
          <Btn onClick={() => { setForm({ voucher_type:'journal',voucher_date:today(),narration:'',ref_number:'',lines:[{account_id:'',debit:'',credit:'',narration:'',party_id:''},{account_id:'',debit:'',credit:'',narration:'',party_id:''}] }); setModal(true); }}>+ New Voucher</Btn>
        </div>
      </div>

      <Card>
        {loading ? <Spinner /> : (
          <Table headers={['Voucher No.','Date','Type','Narration','Dr Amount','Cr Amount','Actions']}>
            {vouchers.map(v => (
              <Tr key={v.id}>
                <Td mono><span className="font-bold text-blue-600">{v.voucher_number}</span></Td>
                <Td>{fmtDate(v.voucher_date)}</Td>
                <Td><Badge color="blue">{typeMap[v.voucher_type] || v.voucher_type}</Badge></Td>
                <Td><span className="text-xs">{v.narration?.slice(0, 55)}{v.narration?.length > 55 ? '…' : ''}</span></Td>
                <Td right><span className="font-semibold text-blue-700">{amt(v.total_debit)}</span></Td>
                <Td right><span className="font-semibold text-rose-600">{amt(v.total_credit)}</span></Td>
                <Td>
                  <Btn size="sm" variant="ghost" onClick={() => setViewModal(v.id)}>View</Btn>
                </Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      {/* New Voucher Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title="New Journal Voucher" size="xl"
        footer={
          <div className="flex items-center justify-between w-full">
            <div className={`text-sm font-bold ${isBalanced ? 'text-green-600' : 'text-red-600'}`}>
              {isBalanced ? '✅ Balanced' : `⚠️ Dr ${numFmt(totalDr)} ≠ Cr ${numFmt(totalCr)}`}
            </div>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn loading={saving} onClick={save} variant={isBalanced ? 'primary' : 'outline'}>✓ Save Voucher</Btn>
            </div>
          </div>
        }>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <FormGroup label="Voucher Type" required>
            <Select value={form.voucher_type} onChange={e => setForm(f=>({...f,voucher_type:e.target.value}))}>
              {Object.entries(typeMap).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Date" required><Input type="date" value={form.voucher_date} onChange={e=>setForm(f=>({...f,voucher_date:e.target.value}))} /></FormGroup>
          <FormGroup label="Ref No. / Bill No."><Input value={form.ref_number} onChange={e=>setForm(f=>({...f,ref_number:e.target.value}))} placeholder="e.g. INV-041" /></FormGroup>
        </div>
        <FormGroup label="Narration"><Input value={form.narration} onChange={e=>setForm(f=>({...f,narration:e.target.value}))} placeholder="Being..." /></FormGroup>

        <SectionDivider title="Ledger Entries (Debit / Credit)" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="px-3 py-2 text-left text-xs font-semibold w-64">Account</th>
                <th className="px-2 py-2 text-xs w-40">Party (optional)</th>
                <th className="px-2 py-2 text-xs w-28">Debit (₹)</th>
                <th className="px-2 py-2 text-xs w-28">Credit (₹)</th>
                <th className="px-2 py-2 text-xs">Narration</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {form.lines.map((line, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-1 py-1">
                    <Select value={line.account_id} onChange={e => setLine(i,'account_id',e.target.value)}>
                      <option value="">— Select Account —</option>
                      {['asset','liability','equity','income','expense'].map(type => (
                        <optgroup key={type} label={type.toUpperCase()}>
                          {accounts.filter(a => a.type === type && !a.is_group).map(a => (
                            <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </Select>
                  </td>
                  <td className="px-1 py-1">
                    <Select value={line.party_id} onChange={e => setLine(i,'party_id',e.target.value)}>
                      <option value="">—</option>
                      {parties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </Select>
                  </td>
                  <td className="px-1 py-1"><Input type="number" value={line.debit} onChange={e => setLine(i,'debit',e.target.value)} placeholder="0.00" className="text-right" /></td>
                  <td className="px-1 py-1"><Input type="number" value={line.credit} onChange={e => setLine(i,'credit',e.target.value)} placeholder="0.00" className="text-right" /></td>
                  <td className="px-1 py-1"><Input value={line.narration} onChange={e => setLine(i,'narration',e.target.value)} placeholder="Being..." /></td>
                  <td className="px-1"><button onClick={() => delLine(i)} className="text-red-400 hover:text-red-600 text-sm">✕</button></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-bold">
                <td className="px-3 py-2 text-sm text-slate-500" colSpan={2}>Total</td>
                <td className="px-2 py-2 text-right text-blue-700">{numFmt(totalDr)}</td>
                <td className="px-2 py-2 text-right text-rose-600">{numFmt(totalCr)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <Btn variant="ghost" size="sm" className="mt-2" onClick={addLine}>+ Add Line</Btn>
      </Modal>

      {/* View Voucher Modal */}
      {viewModal && <VoucherDetail id={viewModal} onClose={() => setViewModal(null)} accounts={accounts} typeMap={typeMap} />}
    </div>
  );
}

function VoucherDetail({ id, onClose, accounts, typeMap }) {
  const [data, loading] = useData(() => accountsAPI.voucherGet(id), [id]);
  const v = data?.voucher;
  return (
    <Modal open={!!id} onClose={onClose} title="Voucher Detail" size="lg"
      footer={<><PrintBtn /><Btn variant="ghost" onClick={onClose}>Close</Btn></>}>
      {loading ? <Spinner /> : v && (
        <>
          <StmtHeader title={`${typeMap[v.voucher_type] || v.voucher_type}`} subtitle={`Voucher No: ${v.voucher_number} | Date: ${fmtDate(v.voucher_date)}`} />
          <div className="mb-3 text-sm text-slate-500"><strong>Narration:</strong> {v.narration}</div>
          <table className="w-full text-sm border-collapse">
            <thead><tr className="bg-slate-800 text-white"><th className="px-4 py-2 text-left">Account</th><th className="px-4 py-2 text-right">Debit (₹)</th><th className="px-4 py-2 text-right">Credit (₹)</th></tr></thead>
            <tbody>
              {(v.lines || []).map((l, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-4 py-2"><span className="font-mono text-xs text-slate-400">{l.acc_code}</span> {l.acc_name}{l.party_name ? <span className="text-slate-400 text-xs ml-1">({l.party_name})</span> : ''}</td>
                  <td className="px-4 py-2 text-right text-blue-700">{l.debit > 0 ? numFmt(l.debit) : ''}</td>
                  <td className="px-4 py-2 text-right text-rose-600">{l.credit > 0 ? numFmt(l.credit) : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="bg-slate-50 font-bold border-t-2 border-slate-800"><td className="px-4 py-2">Total</td><td className="px-4 py-2 text-right text-blue-700">{numFmt(v.total_debit)}</td><td className="px-4 py-2 text-right text-rose-600">{numFmt(v.total_credit)}</td></tr></tfoot>
          </table>
        </>
      )}
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════
// LEDGER
// ══════════════════════════════════════════════════════════
export function Ledger() {
  const [accData] = useData(() => accountsAPI.chart());
  const [selAcc, setSelAcc] = useState('');
  const [from, setFrom] = useState(fyStart());
  const [to, setTo]     = useState(today());
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading]       = useState(false);

  const accounts = (accData?.accounts || []).filter(a => !a.is_group && a.is_active);

  const loadLedger = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await accountsAPI.ledger(id, from, to);
      setLedgerData(r.data);
    } catch { toast.error('Failed to load ledger'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (selAcc) loadLedger(selAcc); }, [selAcc, from, to]); // eslint-disable-line

  const ld = ledgerData;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">📖 Account Ledger</h1></div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <FormGroup label="Select Account">
            <Select value={selAcc} onChange={e => setSelAcc(e.target.value)} style={{ width: 260 }}>
              <option value="">— Select Account —</option>
              {['asset','liability','equity','income','expense'].map(type => (
                <optgroup key={type} label={type.toUpperCase()}>
                  {accounts.filter(a => a.type === type).map(a => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                </optgroup>
              ))}
            </Select>
          </FormGroup>
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
          <PrintBtn />
        </div>
      </div>

      {loading && <Spinner />}
      {ld && !loading && (
        <Card>
          <div className="p-5">
            <StmtHeader title={`Ledger — ${ld.account?.name}`} subtitle={`Account Code: ${ld.account?.code} | Period: ${fmtDate(from)} to ${fmtDate(to)}`} />
            <div className="flex gap-4 text-sm mb-4 flex-wrap">
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2"><span className="text-slate-400">Opening Balance</span><div className="font-bold text-blue-700">{ld.opening_balance >= 0 ? `Dr ${amt(ld.opening_balance)}` : `Cr ${amt(Math.abs(ld.opening_balance))}`}</div></div>
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2"><span className="text-slate-400">Total Debit</span><div className="font-bold text-green-700">{amt(ld.total_debit)}</div></div>
              <div className="bg-rose-50 border border-rose-200 rounded-lg px-4 py-2"><span className="text-slate-400">Total Credit</span><div className="font-bold text-rose-700">{amt(ld.total_credit)}</div></div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-2"><span className="text-slate-400">Closing Balance</span><div className="font-bold text-purple-700">{ld.closing_balance >= 0 ? `Dr ${amt(ld.closing_balance)}` : `Cr ${amt(Math.abs(ld.closing_balance))}`}</div></div>
            </div>
            <table className="w-full text-sm border-collapse">
              <thead><tr className="bg-slate-800 text-white text-xs"><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Voucher No.</th><th className="px-3 py-2 text-left">Narration</th><th className="px-3 py-2 text-left">Party</th><th className="px-3 py-2 text-right">Debit</th><th className="px-3 py-2 text-right">Credit</th><th className="px-3 py-2 text-right">Balance</th></tr></thead>
              <tbody>
                <tr className="bg-slate-50 border-b border-slate-200 font-semibold text-sm">
                  <td className="px-3 py-2">{fmtDate(from)}</td>
                  <td className="px-3 py-2 font-mono text-xs">Opening</td>
                  <td className="px-3 py-2" colSpan={3}>Opening Balance</td>
                  <td className="px-3 py-2 text-right"></td>
                  <td className="px-3 py-2 text-right text-blue-700 font-bold">{ld.opening_balance >= 0 ? `Dr ${numFmt(ld.opening_balance)}` : `Cr ${numFmt(Math.abs(ld.opening_balance))}`}</td>
                </tr>
                {ld.ledger.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-blue-50/40">
                    <td className="px-3 py-2">{fmtDate(r.voucher_date)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-blue-600">{r.voucher_number}</td>
                    <td className="px-3 py-2 text-xs">{r.line_narration || r.narration}</td>
                    <td className="px-3 py-2 text-xs text-slate-400">{r.party_name || '—'}</td>
                    <td className="px-3 py-2 text-right text-blue-700">{r.debit > 0 ? numFmt(r.debit) : ''}</td>
                    <td className="px-3 py-2 text-right text-rose-600">{r.credit > 0 ? numFmt(r.credit) : ''}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${r.balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>{r.balance >= 0 ? `Dr ${numFmt(r.balance)}` : `Cr ${numFmt(Math.abs(r.balance))}`}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-800 text-white font-bold">
                  <td className="px-3 py-2" colSpan={4}>Closing Balance</td>
                  <td className="px-3 py-2 text-right">{numFmt(ld.total_debit)}</td>
                  <td className="px-3 py-2 text-right">{numFmt(ld.total_credit)}</td>
                  <td className={`px-3 py-2 text-right ${ld.closing_balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>{ld.closing_balance >= 0 ? `Dr ${numFmt(ld.closing_balance)}` : `Cr ${numFmt(Math.abs(ld.closing_balance))}`}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TRIAL BALANCE
// ══════════════════════════════════════════════════════════
export function TrialBalance() {
  const [from, setFrom] = useState(fyStart());
  const [to, setTo]     = useState(today());
  const [data, loading, reload] = useData(() => accountsAPI.trialBalance(from, to), [from, to]);
  const tb = data?.trial_balance || [];
  const totalDr = data?.total_dr || 0;
  const totalCr = data?.total_cr || 0;
  const balanced = Math.abs(totalDr - totalCr) < 1;

  const grouped = ['asset','liability','equity','income','expense'].map(t => ({
    type: t, rows: tb.filter(r => r.type === t)
  })).filter(g => g.rows.length > 0);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">⚖️ Trial Balance</h1></div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
          <PrintBtn />
        </div>
      </div>
      {!balanced && !loading && <Alert type="warn">⚠️ Trial Balance does not match! Dr: {amt(totalDr)} ≠ Cr: {amt(totalCr)}</Alert>}
      {balanced && !loading && <Alert type="success">✅ Trial Balance is balanced: {amt(totalDr)}</Alert>}

      <Card>
        <div className="p-5">
          <StmtHeader title="Trial Balance" subtitle={`Period: ${fmtDate(from)} to ${fmtDate(to)}`} />
          {loading ? <Spinner /> : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-800 text-white text-xs">
                  <th className="px-3 py-2 text-left w-20">Code</th>
                  <th className="px-3 py-2 text-left">Account Name</th>
                  <th className="px-3 py-2 text-right">Opening Dr</th>
                  <th className="px-3 py-2 text-right">Opening Cr</th>
                  <th className="px-3 py-2 text-right">Period Dr</th>
                  <th className="px-3 py-2 text-right">Period Cr</th>
                  <th className="px-3 py-2 text-right">Closing Dr</th>
                  <th className="px-3 py-2 text-right">Closing Cr</th>
                </tr>
              </thead>
              <tbody>
                {grouped.map(g => (
                  <React.Fragment key={g.type}>
                    <tr className="bg-slate-100">
                      <td colSpan={8} className="px-3 py-2 font-bold text-xs uppercase tracking-wider text-slate-600">
                        <Badge color={typeColor[g.type]}>{g.type}</Badge>
                      </td>
                    </tr>
                    {g.rows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-blue-50/30">
                        <td className="px-3 py-2 font-mono text-xs text-slate-400">{r.code}</td>
                        <td className="px-3 py-2">{r.name}</td>
                        <td className="px-3 py-2 text-right text-xs">{r.opening_dr > 0 ? numFmt(r.opening_dr) : ''}</td>
                        <td className="px-3 py-2 text-right text-xs">{r.opening_cr > 0 ? numFmt(r.opening_cr) : ''}</td>
                        <td className="px-3 py-2 text-right">{r.period_dr > 0 ? numFmt(r.period_dr) : ''}</td>
                        <td className="px-3 py-2 text-right">{r.period_cr > 0 ? numFmt(r.period_cr) : ''}</td>
                        <td className="px-3 py-2 text-right font-semibold text-blue-700">{r.closing_dr > 0 ? numFmt(r.closing_dr) : ''}</td>
                        <td className="px-3 py-2 text-right font-semibold text-rose-600">{r.closing_cr > 0 ? numFmt(r.closing_cr) : ''}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-800 text-white font-bold text-sm">
                  <td className="px-3 py-3" colSpan={6}>Grand Total</td>
                  <td className={`px-3 py-3 text-right ${balanced ? 'text-green-400' : 'text-red-400'}`}>{numFmt(totalDr)}</td>
                  <td className={`px-3 py-3 text-right ${balanced ? 'text-green-400' : 'text-red-400'}`}>{numFmt(totalCr)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PROFIT & LOSS ACCOUNT
// ══════════════════════════════════════════════════════════
export function ProfitLoss() {
  const [from, setFrom] = useState(fyStart());
  const [to, setTo]     = useState(today());
  const [data, loading] = useData(() => accountsAPI.profitLoss(from, to), [from, to]);
  const pl = data || {};

  const Section = ({ title, rows, total, colorClass }) => (
    <>
      <tr className="bg-slate-100"><td className="px-4 py-2 font-bold text-xs uppercase tracking-wider text-slate-600" colSpan={2}>{title}</td></tr>
      {rows.map((r, i) => (
        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
          <td className="px-4 py-2 pl-8 text-sm text-slate-600">{r.name}</td>
          <td className={`px-4 py-2 text-right text-sm font-semibold ${colorClass}`}>{numFmt(r.amount)}</td>
        </tr>
      ))}
      <tr className="border-b-2 border-slate-300 bg-slate-50">
        <td className="px-4 py-2 font-bold text-sm">Total {title}</td>
        <td className={`px-4 py-2 text-right font-bold text-sm ${colorClass}`}>{numFmt(total)}</td>
      </tr>
    </>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">📊 Profit & Loss Account</h1></div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
          <PrintBtn />
        </div>
      </div>

      <Card>
        <div className="p-5">
          <StmtHeader title="Profit & Loss Account" subtitle={`For the period: ${fmtDate(from)} to ${fmtDate(to)}`} />
          {loading ? <Spinner /> : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* DEBIT SIDE */}
              <div>
                <div className="text-center font-bold text-sm bg-rose-700 text-white py-2 rounded-t-lg">DEBIT (EXPENSES)</div>
                <table className="w-full border border-slate-200">
                  <tbody>
                    <Section title="Cost of Goods Sold" rows={pl.direct_expense || []} total={pl.total_direct_expense || 0} colorClass="text-rose-700" />
                    <Section title="Operating Expenses" rows={pl.indirect_expense || []} total={pl.total_indirect_expense || 0} colorClass="text-rose-600" />
                    <tr className="bg-rose-50 border-t-2 border-rose-300">
                      <td className="px-4 py-3 font-bold text-sm">Total Expenses</td>
                      <td className="px-4 py-3 text-right font-bold text-rose-700">{numFmt(pl.total_expense)}</td>
                    </tr>
                    <tr className="bg-green-50 border-t border-green-300">
                      <td className="px-4 py-3 font-black text-sm text-green-800">{parseFloat(pl.net_profit) >= 0 ? '🏆 Net Profit' : '⚠️ Net Loss'}</td>
                      <td className={`px-4 py-3 text-right font-black text-sm ${parseFloat(pl.net_profit) >= 0 ? 'text-green-800' : 'text-red-800'}`}>{numFmt(Math.abs(pl.net_profit))}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="mt-3 p-3 bg-slate-50 rounded text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Gross Profit</span><span className="font-bold text-blue-700">{numFmt(pl.gross_profit)}</span></div>
                  {pl.total_direct_income > 0 && <div className="flex justify-between mt-1"><span className="text-slate-500">Gross Profit %</span><span className="font-bold">{((pl.gross_profit / pl.total_direct_income) * 100).toFixed(2)}%</span></div>}
                  {pl.total_income > 0 && <div className="flex justify-between mt-1"><span className="text-slate-500">Net Profit %</span><span className="font-bold text-green-700">{((pl.net_profit / pl.total_income) * 100).toFixed(2)}%</span></div>}
                </div>
              </div>
              {/* CREDIT SIDE */}
              <div>
                <div className="text-center font-bold text-sm bg-green-700 text-white py-2 rounded-t-lg">CREDIT (INCOME)</div>
                <table className="w-full border border-slate-200">
                  <tbody>
                    <Section title="Revenue from Operations" rows={pl.direct_income || []} total={pl.total_direct_income || 0} colorClass="text-green-700" />
                    <Section title="Other Income" rows={pl.other_income || []} total={pl.total_other_income || 0} colorClass="text-green-600" />
                    <tr className="bg-green-50 border-t-2 border-green-300">
                      <td className="px-4 py-3 font-bold text-sm">Total Income</td>
                      <td className="px-4 py-3 text-right font-bold text-green-700">{numFmt(pl.total_income)}</td>
                    </tr>
                    {parseFloat(pl.net_profit) < 0 && (
                      <tr className="bg-red-50 border-t border-red-200">
                        <td className="px-4 py-3 font-black text-sm text-red-800">⚠️ Net Loss</td>
                        <td className="px-4 py-3 text-right font-black text-sm text-red-800">{numFmt(Math.abs(pl.net_profit))}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// BALANCE SHEET
// ══════════════════════════════════════════════════════════
export function BalanceSheet() {
  const [from, setFrom] = useState(fyStart());
  const [to, setTo]     = useState(today());
  const [data, loading] = useData(() => accountsAPI.balanceSheet(from, to), [from, to]);
  const bs = data || {};

  const Section = ({ title, rows, colorClass }) => (
    <>
      <tr className="bg-slate-100"><td className="px-4 py-2 font-bold text-xs uppercase tracking-wider text-slate-600" colSpan={2}>{title}</td></tr>
      {rows.map((r, i) => (
        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
          <td className="px-4 py-2 pl-8 text-sm text-slate-600">{r.name}</td>
          <td className={`px-4 py-2 text-right text-sm font-semibold ${colorClass}`}>{numFmt(Math.abs(r.balance))}</td>
        </tr>
      ))}
    </>
  );

  const totalLiabEquity = (parseFloat(bs.total_liabilities) || 0) + (parseFloat(bs.total_equity) || 0);
  const isBalanced = Math.abs((parseFloat(bs.total_assets) || 0) - totalLiabEquity) < 1;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">🏛️ Balance Sheet</h1></div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
          <PrintBtn />
        </div>
      </div>
      {!isBalanced && !loading && <Alert type="warn">⚠️ Balance Sheet does not tally! Assets: {amt(bs.total_assets)} ≠ Liabilities+Equity: {amt(totalLiabEquity)}</Alert>}
      {isBalanced && !loading && <Alert type="success">✅ Balance Sheet tallied: {amt(bs.total_assets)}</Alert>}

      <Card>
        <div className="p-5">
          <StmtHeader title="Balance Sheet" subtitle={`As on ${fmtDate(to)}`} />
          {loading ? <Spinner /> : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* ASSETS */}
              <div>
                <div className="text-center font-bold text-sm bg-blue-700 text-white py-2 rounded-t-lg">ASSETS</div>
                <table className="w-full border border-slate-200">
                  <tbody>
                    <Section title="Fixed Assets" rows={bs.fixed_assets || []} colorClass="text-blue-700" />
                    <Section title="Current Assets" rows={bs.current_assets || []} colorClass="text-blue-600" />
                    <tr className="bg-blue-700 text-white">
                      <td className="px-4 py-3 font-black">TOTAL ASSETS</td>
                      <td className="px-4 py-3 text-right font-black">{numFmt(bs.total_assets)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* LIABILITIES + EQUITY */}
              <div>
                <div className="text-center font-bold text-sm bg-amber-700 text-white py-2 rounded-t-lg">LIABILITIES & EQUITY</div>
                <table className="w-full border border-slate-200">
                  <tbody>
                    <Section title="Share Capital & Reserves" rows={bs.equity || []} colorClass="text-purple-700" />
                    <tr className="bg-slate-100 border-b">
                      <td className="px-4 py-2 font-bold text-sm pl-8">Current Year Net Profit</td>
                      <td className={`px-4 py-2 text-right font-bold text-sm ${parseFloat(bs.net_profit) >= 0 ? 'text-green-700' : 'text-red-700'}`}>{numFmt(bs.net_profit)}</td>
                    </tr>
                    <tr className="border-b-2 border-slate-300 bg-slate-50">
                      <td className="px-4 py-2 font-bold text-sm">Total Equity</td>
                      <td className="px-4 py-2 text-right font-bold text-purple-700">{numFmt(bs.total_equity)}</td>
                    </tr>
                    <Section title="Long-Term Liabilities" rows={bs.long_term_liab || []} colorClass="text-amber-700" />
                    <Section title="Current Liabilities" rows={bs.current_liab || []} colorClass="text-rose-700" />
                    <tr className="bg-amber-700 text-white">
                      <td className="px-4 py-3 font-black">TOTAL LIABILITIES & EQUITY</td>
                      <td className="px-4 py-3 text-right font-black">{numFmt(totalLiabEquity)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// CASH FLOW STATEMENT
// ══════════════════════════════════════════════════════════
export function CashFlow() {
  const [from, setFrom] = useState(fyStart());
  const [to, setTo]     = useState(today());
  const [data, loading] = useData(() => accountsAPI.cashFlow(from, to), [from, to]);
  const cf = data || {};
  const op = cf.operating || {};

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">💧 Cash Flow Statement</h1></div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
          <PrintBtn />
        </div>
      </div>
      <Card>
        <div className="p-5">
          <StmtHeader title="Cash Flow Statement" subtitle={`Period: ${fmtDate(from)} to ${fmtDate(to)}`} />
          {loading ? <Spinner /> : (
            <table className="w-full text-sm border-collapse max-w-lg mx-auto">
              <thead><tr className="bg-slate-800 text-white"><th className="px-4 py-2 text-left">Particulars</th><th className="px-4 py-2 text-right">Amount (₹)</th></tr></thead>
              <tbody>
                {/* Operating */}
                <tr className="bg-blue-50"><td className="px-4 py-2 font-bold" colSpan={2}>A. Cash from Operating Activities</td></tr>
                <tr className="border-b border-slate-100"><td className="px-4 py-2 pl-8">Receipts from Customers</td><td className="px-4 py-2 text-right text-green-700">+ {numFmt(op.sales_receipts)}</td></tr>
                <tr className="border-b border-slate-100"><td className="px-4 py-2 pl-8">Payments to Suppliers</td><td className="px-4 py-2 text-right text-rose-600">− {numFmt(op.purchase_payments)}</td></tr>
                <tr className="border-b border-slate-100"><td className="px-4 py-2 pl-8">Operating Expenses Paid</td><td className="px-4 py-2 text-right text-rose-600">− {numFmt(op.expense_payments)}</td></tr>
                <tr className="bg-blue-100 font-bold border-t border-blue-300"><td className="px-4 py-2">Net Cash from Operations (A)</td><td className={`px-4 py-2 text-right ${parseFloat(op.net_operating) >= 0 ? 'text-green-700' : 'text-red-700'}`}>{numFmt(op.net_operating)}</td></tr>
                {/* Investing */}
                <tr className="bg-amber-50 mt-2"><td className="px-4 py-2 font-bold" colSpan={2}>B. Cash from Investing Activities</td></tr>
                <tr className="border-b border-slate-100"><td className="px-4 py-2 pl-8 text-slate-400">No investing activities recorded</td><td className="px-4 py-2 text-right">—</td></tr>
                <tr className="bg-amber-100 font-bold"><td className="px-4 py-2">Net Cash from Investing (B)</td><td className="px-4 py-2 text-right">0.00</td></tr>
                {/* Financing */}
                <tr className="bg-purple-50"><td className="px-4 py-2 font-bold" colSpan={2}>C. Cash from Financing Activities</td></tr>
                <tr className="border-b border-slate-100"><td className="px-4 py-2 pl-8 text-slate-400">No financing activities recorded</td><td className="px-4 py-2 text-right">—</td></tr>
                <tr className="bg-purple-100 font-bold"><td className="px-4 py-2">Net Cash from Financing (C)</td><td className="px-4 py-2 text-right">0.00</td></tr>
                {/* Summary */}
                <tr className="border-t-2 border-slate-400 bg-slate-100 font-bold"><td className="px-4 py-3">Net Increase / (Decrease) in Cash (A+B+C)</td><td className={`px-4 py-3 text-right ${parseFloat(cf.net_cash_change) >= 0 ? 'text-green-700' : 'text-red-700'}`}>{numFmt(cf.net_cash_change)}</td></tr>
                <tr className="border-b border-slate-200"><td className="px-4 py-2">Add: Opening Cash & Bank Balance</td><td className="px-4 py-2 text-right">{numFmt(cf.opening_cash)}</td></tr>
                <tr className="bg-slate-800 text-white font-black"><td className="px-4 py-3">Closing Cash & Bank Balance</td><td className="px-4 py-3 text-right text-green-400">{numFmt(cf.closing_cash)}</td></tr>
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// DAY BOOK
// ══════════════════════════════════════════════════════════
export function DayBook() {
  const [date, setDate] = useState(today());
  const [data, loading, reload] = useData(() => accountsAPI.dayBook(date), [date]);
  const entries = data?.day_book || [];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">📅 Day Book</h1><p className="text-sm text-slate-400">All transactions for a selected date</p></div>
        <div className="ml-auto flex gap-2">
          <FormGroup label="Date"><Input type="date" value={date} onChange={e => setDate(e.target.value)} /></FormGroup>
          <PrintBtn />
        </div>
      </div>
      <Card>
        <div className="p-5">
          <StmtHeader title="Day Book" subtitle={`Date: ${fmtDate(date)}`} />
          {loading ? <Spinner /> : (
            <table className="w-full text-sm border-collapse">
              <thead><tr className="bg-slate-800 text-white text-xs"><th className="px-3 py-2 text-left">Voucher No.</th><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">Narration</th><th className="px-3 py-2 text-left">Accounts</th><th className="px-3 py-2 text-right">Debit</th><th className="px-3 py-2 text-right">Credit</th></tr></thead>
              <tbody>
                {entries.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No transactions for this date</td></tr>}
                {entries.map((e, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-blue-50/30">
                    <td className="px-3 py-2 font-mono text-xs text-blue-600 font-bold">{e.voucher_number}</td>
                    <td className="px-3 py-2"><Badge color="blue">{e.voucher_type}</Badge></td>
                    <td className="px-3 py-2 text-xs">{e.narration}</td>
                    <td className="px-3 py-2 text-xs text-slate-400">{e.accounts_involved}</td>
                    <td className="px-3 py-2 text-right text-blue-700 font-semibold">{numFmt(e.total_debit)}</td>
                    <td className="px-3 py-2 text-right text-rose-600 font-semibold">{numFmt(e.total_credit)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-800 text-white font-bold">
                  <td className="px-3 py-2" colSpan={4}>Total for {fmtDate(date)}</td>
                  <td className="px-3 py-2 text-right text-green-400">{numFmt(data?.total_debit)}</td>
                  <td className="px-3 py-2 text-right text-rose-300">{numFmt(data?.total_credit)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TDS MANAGEMENT
// ══════════════════════════════════════════════════════════
export function TDS() {
  const [data, loading, reload] = useData(() => accountsAPI.tds());
  const [partyData] = useData(() => partiesAPI.list());
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState({ party_id: '', section: '194C', description: '', tds_rate: 1, amount: '', payment_date: today() });
  const [saving, setSaving] = useState(false);

  const entries = data?.tds_entries || [];
  const parties = partyData?.parties || [];
  const tdsAmt  = (parseFloat(form.amount || 0) * parseFloat(form.tds_rate || 0) / 100).toFixed(2);

  const sections = ['192','192A','193','194','194A','194B','194BB','194C','194D','194DA','194E','194EE','194F','194G','194H','194I','194IA','194IB','194IC','194J','194LA','194N','194O','195','196B','196C','196D'];

  const save = async () => {
    if (!form.party_id || !form.amount) return toast.error('Party and amount required');
    setSaving(true);
    try {
      await accountsAPI.tdsCreate(form);
      toast.success('TDS entry saved!');
      setModal(false); reload();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">🧾 TDS Management</h1><p className="text-sm text-slate-400">Tax Deducted at Source — Section-wise tracking</p></div>
        <div className="ml-auto"><Btn onClick={() => setModal(true)}>+ Add TDS Entry</Btn></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <StatCard icon="🧾" label="Total TDS Deducted" value={amt(data?.total_tds)} color="blue" />
        <StatCard icon="⏳" label="Pending Deposit" value={amt(data?.pending_deposit)} color="amber" />
        <StatCard icon="✅" label="Deposited" value={amt((parseFloat(data?.total_tds||0) - parseFloat(data?.pending_deposit||0)).toFixed(2))} color="green" />
      </div>
      <Card>
        {loading ? <Spinner /> : (
          <Table headers={['Party','Section','Description','Amount','TDS Rate','TDS Amount','Date','Challan','Status']}>
            {entries.map(r => (
              <Tr key={r.id}>
                <Td><span className="font-semibold">{r.party_name}</span></Td>
                <Td mono>{r.section}</Td>
                <Td>{r.description}</Td>
                <Td right>{amt(r.amount)}</Td>
                <Td>{r.tds_rate}%</Td>
                <Td right><span className="font-bold text-blue-700">{amt(r.tds_amount)}</span></Td>
                <Td>{fmtDate(r.payment_date)}</Td>
                <Td mono>{r.challan_no || '—'}</Td>
                <Td><StatusBadge status={r.status} /></Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title="New TDS Entry" size="md"
        footer={<><Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn><Btn loading={saving} onClick={save}>Save TDS</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Party" required>
            <Select value={form.party_id} onChange={e => setForm(f=>({...f,party_id:e.target.value}))}>
              <option value="">— Select Party —</option>
              {parties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="TDS Section">
            <Select value={form.section} onChange={e => setForm(f=>({...f,section:e.target.value}))}>
              {sections.map(s => <option key={s} value={s}>Sec {s}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Description"><Input value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="e.g. Contractor Payment" /></FormGroup>
          <FormGroup label="Payment Date"><Input type="date" value={form.payment_date} onChange={e=>setForm(f=>({...f,payment_date:e.target.value}))} /></FormGroup>
          <FormGroup label="Payment Amount (₹)" required><Input type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} /></FormGroup>
          <FormGroup label="TDS Rate (%)"><Input type="number" value={form.tds_rate} onChange={e=>setForm(f=>({...f,tds_rate:e.target.value}))} /></FormGroup>
          <FormGroup label="TDS Amount (Auto)"><Input readOnly value={`₹ ${tdsAmt}`} className="bg-green-50 font-bold text-green-700" /></FormGroup>
        </div>
        <Alert type="info">TDS of ₹{tdsAmt} will be deducted from the payment and a liability entry will be created.</Alert>
      </Modal>
    </div>
  );
}
